import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth, Hub } from 'aws-amplify';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { authCache } from '../../utils/authCache';
import RoleSelection from './RoleSelection';

const OAuthHandler = () => {
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);
  const navigate = useNavigate();
  const { checkAuthState, user, activeRole } = useAuth();
  
  // 🚀 OPTIMIZATION: Use context state for redirect logic
  useEffect(() => {
    if (user && !isProcessing) {
      if (activeRole) {
        // User has a role, redirect to their dashboard
        console.log(`OAuth redirect to /${activeRole}`);
        navigate(`/${activeRole}`, { replace: true });
      } else {
        // User doesn't have a role, show role selection
        console.log('OAuth user has no role, showing role selection');
        setNeedsRoleSelection(true);
      }
    }
  }, [user, activeRole, navigate, isProcessing]);
  
  useEffect(() => {
    let isRedirecting = false;
    let hubUnsubscribe = null;
    
    const handleAuth = async () => {
      console.log("🚀 Optimized OAuthHandler initialized");
      
      try {
        // 🚀 OPTIMIZATION: Simplified Hub listener - let AuthContext handle the heavy lifting
        hubUnsubscribe = Hub.listen('auth', async ({ payload }) => {
          console.log("Auth Hub event:", payload.event);
          const { event } = payload;
          
          if ((event === 'signIn' || event === 'cognitoHostedUI') && !isRedirecting) {
            isRedirecting = true;
            console.log("OAuth sign-in detected, refreshing auth state");
            
            try {
              // Let AuthContext handle all the attribute parsing and caching
              await checkAuthState(true); // Force refresh to bypass cache
              setIsProcessing(false);
            } catch (err) {
              console.error('Error refreshing auth state:', err);
              setError('Authentication failed. Please try again.');
              setIsProcessing(false);
            }
          } else if (event === 'signIn_failure') {
            console.error('OAuth sign-in failure:', payload.data);
            setError('Authentication failed. Please try again.');
            setIsProcessing(false);
          }
        });
        
        // 🚀 OPTIMIZATION: Quick check if already authenticated using cached data
        const cachedAttributes = authCache.getCachedUserAttributes();
        if (cachedAttributes) {
          console.log("Found cached auth data, redirecting immediately");
          const { activeRole } = authCache.extractRoleInfo(cachedAttributes);
          if (activeRole && !isRedirecting) {
            isRedirecting = true;
            navigate(`/${activeRole}`, { replace: true });
            setIsProcessing(false);
            return;
          }
        }
        
        // Check if we're already authenticated
        try {
          await Auth.currentAuthenticatedUser();
          console.log("User already authenticated, updating auth state");
          
          if (!isRedirecting) {
            isRedirecting = true;
            await checkAuthState(true); // Force refresh
            setIsProcessing(false);
          }
        } catch (e) {
          console.log("Waiting for OAuth callback...");
          // We'll wait for the Hub events
        }
        
      } catch (err) {
        console.error('OAuth handler error:', err);
        setError('Authentication error occurred.');
        setIsProcessing(false);
      }
    };
    
    handleAuth();
    
    // Timeout for error handling
    const timeout = setTimeout(() => {
      if (isProcessing) {
        console.log("OAuth handling timed out");
        setError("Authentication process timed out. Please try again.");
        setIsProcessing(false);
        navigate('/login', { replace: true });
      }
    }, 10000);
    
    return () => {
      clearTimeout(timeout);
      if (hubUnsubscribe) {
        hubUnsubscribe();
      }
    };
  }, [navigate, checkAuthState, isProcessing]);
  
  // Handle role selection completion
  const handleRoleSelected = async (role) => {
    console.log(`Role selected: ${role}, refreshing auth state...`);
    // Refresh auth state to get updated role information
    await checkAuthState(true);
    // The useEffect above will handle the redirect
  };

  // Show role selection if user needs to choose a role
  if (needsRoleSelection && !error) {
    return <RoleSelection onRoleSelected={handleRoleSelected} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
      {error ? (
        <>
          <Typography color="error">{error}</Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate('/login')}
          >
            Return to Login
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Completing authentication...</Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthHandler;
